import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { VehicleBuddyLogo } from '../components/VehicleBuddyLogo';
import { authAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const navigate = useNavigate();
  const { login, user, loading } = useAuth();

  // If the user already has a valid session cookie (e.g. landed here after a
  // tab refresh), send them straight to the app instead of showing the login
  // form. Prevents the "I'm stuck on /login even though I'm logged in" UX.
  useEffect(() => {
    if (!loading && user) {
      navigate('/app', { replace: true });
    }
  }, [loading, user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    // Trim inputs
    const email = formData.email.trim();
    const password = formData.password;
    const name = formData.name.trim();

    // Check required fields
    if (!email || !password) {
      toast.error('Please fill in all required fields');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate password length
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }

    // Check name for registration
    if (!isLogin && !name) {
      toast.error('Please enter your full name');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let response;
      
      if (isLogin) {
        // Login
        response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
        toast.success('Welcome back!');
      } else {
        // Register
        response = await authAPI.register({
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
        toast.success('Account created successfully!');
      }

      const { user } = response.data;
      
      // Cookie is set automatically by backend (httpOnly)
      // No need to store in localStorage anymore
      
      // Update context
      login(user);

      // Navigate to dashboard (using /app which is the index route)
      navigate('/app', { replace: true });
      
    } catch (error) {
      console.error('Auth error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
      
      // Show more user-friendly error messages
      if (errorMessage.includes('Email already registered')) {
        toast.error('This email is already registered. Please sign in instead or use a different email.');
      } else if (errorMessage.includes('Invalid email or password')) {
        toast.error('Invalid email or password. Please try again.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 hero-gradient">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-lg p-8 card-elevated">
          <div className="flex flex-col items-center mb-8">
            <div className="mb-4">
              <VehicleBuddyLogo showText={false} className="h-16 w-16" />
            </div>
            <h1 className="text-3xl font-semibold text-center mb-2">VehicleBuddy</h1>
            <p className="text-muted-foreground text-center">
              Your Complete Vehicle Ownership Companion
            </p>
          </div>

          {/* Google Sign-In - PRIMARY */}
          <div className="mb-6">
            <Button
              type="button"
              variant="default"
              className="w-full h-14 text-lg font-medium bg-primary hover:bg-primary/90"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-signin-btn"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or sign in with email</span>
            </div>
          </div>

          {/* Email/Password Toggle - SECONDARY */}
          <div className="mb-4">
            <div className="flex border rounded-lg p-1 bg-muted">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isLogin 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="login-tab"
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !isLogin 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="register-tab"
              >
                Create Account
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isLogin ? 'Enter password' : 'Min. 8 characters'}
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-muted-foreground">
                  Use at least 8 characters with a mix of letters and numbers
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base"
              size="lg"
              disabled={isLoading}
              data-testid="submit-button"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline font-medium"
                data-testid="toggle-mode-link"
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-semibold text-primary">∞</div>
                <div className="text-xs text-muted-foreground mt-1">Vehicles</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-primary">🔔</div>
                <div className="text-xs text-muted-foreground mt-1">Reminders</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-primary">📝</div>
                <div className="text-xs text-muted-foreground mt-1">Documents</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          By {isLogin ? 'signing in' : 'creating an account'}, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login;
